import { useMemo, useCallback } from 'react';
import Immutable from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';

import {
  paymentsBulkApproveAction,
  paymentsBulkArchiveAction,
  paymentsBulkDeleteAction,
  paymentsBulkMergeAction,
  paymentsBulkPayAction,
  paymentsBulkResetApprovalFlowAction,
  paymentsBulkSaveForLaterAction,
  paymentsBulkSendToApprovalAction,
  paymentsByIdSelector,
} from '@/domain/payments';

import { PAYMENT_STATUSES } from '@/domain/payments/constants';

import * as ACL from '@/domain/restriction';
import { promisify } from '@/lib/helpers';
import { filteredItem } from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/helpers/bulkActionsHelper';

import usePaymentActionAbility from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/hooks/usePaymentActionAbility';
import useConfirmOptions from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/components/BulkActions/useConfirmOptions';

import type { ConfirmOptions } from 'material-ui-confirm';

type Props = {
  paymentsIds: Immutable.Set<string>;
  clearSelection: () => void;
};

const useBulkActions = ({ paymentsIds, clearSelection }: Props) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const payments = useSelector(paymentsByIdSelector);
  const restriction = useSelector(ACL.myRestriction);

  const selectedPayments = useMemo(
    () => payments.filter((_value: any, key: string) => paymentsIds.has(key)),
    [payments, paymentsIds],
  );

  const confirmOptions = useConfirmOptions(paymentsIds, selectedPayments);

  const {
    canStartApprovalFlow,
    canResetApprovalFlow,
    canPay,
    canApprove,
    canStartPaymentProcess,
    canArchive,
    canUnarchive,
    canSaveForLater,
    canDelete,
    isVisibleApprovalFlowButtons,
    isVisibleSendToApprovalButton,
    isVisibleStartPaymentProcessButton,
    isVisibleResetApprovalFlowButton,
    isVisiblePayButton,
    isVisibleMergeButton,
    isVisibleSaveForLaterButton,
    isVisibleArchiveButton,
    isVisibleUnarchiveButton,
    isVisibleDeleteButton,
  } = usePaymentActionAbility(selectedPayments);

  const isDisabledMergeButton = useMemo(() => {
    if (selectedPayments.size <= 1 || paymentsIds.size <= 1) return true;
    return selectedPayments.every((payment: any) => payment?.paymentStatus?.value !== PAYMENT_STATUSES.PREPARATION);
  }, [selectedPayments, paymentsIds]);

  const canMergePayments = useMemo(() => {
    if (selectedPayments.size <= 1 || paymentsIds.size <= 1) return false;
    const vendorName = selectedPayments.first()?.vendorName;
    const currency = selectedPayments.first()?.currency;
    return selectedPayments.every(
      (payment: any) =>
        payment?.vendorName === vendorName &&
        payment?.currency === currency &&
        payment?.paymentStatus?.value === PAYMENT_STATUSES.PREPARATION,
    );
  }, [selectedPayments, paymentsIds]);

  const makeHandler = useCallback(
    (actionCreator: (arg: any) => any, extras: Record<string, unknown> = {}) =>
      async () => {
        await promisify(
          (arg: Record<string, unknown>) => dispatch(actionCreator({ ...arg, ...extras })),
          { paymentsIds },
        );
        clearSelection();
      },
    [dispatch, paymentsIds, clearSelection],
  );

  const handleSendToApproval = useMemo(() => makeHandler(paymentsBulkSendToApprovalAction), [makeHandler]);
  const handleApprove = useMemo(() => makeHandler(paymentsBulkApproveAction), [makeHandler]);
  const handleResetApprovalFlow = useMemo(() => makeHandler(paymentsBulkResetApprovalFlowAction), [makeHandler]);
  const handlePay = useMemo(() => makeHandler(paymentsBulkPayAction), [makeHandler]);
  const handleMerge = useMemo(() => makeHandler(paymentsBulkMergeAction), [makeHandler]);
  const handleSaveForLater = useMemo(
    () => makeHandler(paymentsBulkSaveForLaterAction, { isActive: true }),
    [makeHandler],
  );
  const handleStartPaymentProcess = useMemo(
    () => makeHandler(paymentsBulkSaveForLaterAction, { isActive: false }),
    [makeHandler],
  );
  const handleArchive = useMemo(() => makeHandler(paymentsBulkArchiveAction, { isActive: true }), [makeHandler]);
  const handleUnarchive = useMemo(() => makeHandler(paymentsBulkArchiveAction, { isActive: false }), [makeHandler]);
  const handleDelete = useMemo(() => makeHandler(paymentsBulkDeleteAction), [makeHandler]);

  const handleClickWithConfirm = useCallback(
    (cb: () => void, options: ConfirmOptions) => () => {
      if (options) {
        confirm({ ...options })
          .then(() => cb())
          .catch(() => {});
      } else {
        cb();
      }
    },
    [confirm],
  );

  return useMemo(() => {
    const items = {
      sendToApproval: {
        action: handleClickWithConfirm(handleSendToApproval, confirmOptions.sendToApproval),
        hidden: !isVisibleSendToApprovalButton,
        disabled: !canStartApprovalFlow,
      },
      startPaymentProcess: {
        action: handleClickWithConfirm(handleStartPaymentProcess, confirmOptions.startPaymentProcess),
        hidden: !isVisibleStartPaymentProcessButton,
        disabled: !canStartPaymentProcess,
      },
      approve: {
        action: handleClickWithConfirm(handleApprove, confirmOptions.approve),
        hidden: !isVisibleApprovalFlowButtons,
        disabled: !canApprove,
      },
      resetApprovalFlow: {
        action: handleClickWithConfirm(handleResetApprovalFlow, confirmOptions.resetApprovalFlow),
        hidden: !isVisibleResetApprovalFlowButton,
        disabled: !canResetApprovalFlow,
      },
      pay: {
        action: handleClickWithConfirm(handlePay, confirmOptions.pay),
        hidden: !isVisiblePayButton,
        disabled: !canPay,
      },
      mergePayments: {
        action: handleClickWithConfirm(
          canMergePayments ? handleMerge : () => {},
          canMergePayments ? confirmOptions.mergePayments : confirmOptions.canNotMergePayments,
        ),
        disabled: isDisabledMergeButton,
        hidden: !isVisibleMergeButton,
      },
      saveForLater: {
        action: handleClickWithConfirm(handleSaveForLater, confirmOptions.saveForLater),
        hidden: !isVisibleSaveForLaterButton,
        disabled: !canSaveForLater,
      },
      archive: {
        action: handleClickWithConfirm(handleArchive, confirmOptions.archive),
        hidden: !isVisibleArchiveButton,
        disabled: !canArchive,
      },
      unarchive: {
        action: handleClickWithConfirm(handleUnarchive, confirmOptions.unarchive),
        hidden: !isVisibleUnarchiveButton,
        disabled: !canUnarchive,
      },
      deletePayments: {
        action: handleClickWithConfirm(handleDelete, confirmOptions.deletePayments),
        hidden: !isVisibleDeleteButton,
        disabled: !canDelete,
      },
    };

    return filteredItem({ restriction })
      .filter((o: any) => !items[o.id as keyof typeof items]?.hidden)
      .map((o: any) => ({ ...o, ...items[o.id as keyof typeof items] }));
  }, [
    handleClickWithConfirm,
    handleSendToApproval,
    isVisibleSendToApprovalButton,
    canStartApprovalFlow,
    confirmOptions,
    handleStartPaymentProcess,
    isVisibleStartPaymentProcessButton,
    canStartPaymentProcess,
    handleApprove,
    isVisibleApprovalFlowButtons,
    canApprove,
    handleResetApprovalFlow,
    isVisibleResetApprovalFlowButton,
    canResetApprovalFlow,
    handlePay,
    isVisiblePayButton,
    canPay,
    handleMerge,
    canMergePayments,
    isDisabledMergeButton,
    isVisibleMergeButton,
    handleSaveForLater,
    isVisibleSaveForLaterButton,
    canSaveForLater,
    handleArchive,
    isVisibleArchiveButton,
    canArchive,
    handleUnarchive,
    isVisibleUnarchiveButton,
    canUnarchive,
    handleDelete,
    isVisibleDeleteButton,
    canDelete,
    restriction,
  ]);
};

export default useBulkActions;
